/**
 * Who is signed in, asked once and shared.
 *
 * The session lives here rather than in each page because `/v1/me` is a round
 * trip and three components mounting together must not make three of them. It
 * is also the only place that knows the difference between "nobody is signed
 * in" and "we have not found out yet" — a distinction {@link RequireAuth}
 * depends on to avoid flashing the login form at someone who is already
 * logged in.
 */

import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { AuthApi, SessionUser } from "./authApi";

export interface Session {
  /** `null` once checked and nobody is signed in; `undefined` until then. */
  user: SessionUser | null | undefined;
  /** True until the first `/v1/me` settles. */
  checking: boolean;
  /** Re-read the session — after signing in or verifying. */
  refresh: () => Promise<void>;
  /**
   * Forget the session locally, without waiting for the server.
   *
   * Callers must already have left any gated route before calling this. The
   * gate watches for exactly this transition, so clearing while still on
   * `/dashboard` redirects to the login form — which is how "sign out" once landed
   * on a page inviting you back in. Navigating first is not enough on its own
   * either: React Router publishes the location through `useSyncExternalStore`,
   * outside React's batching, so the two updates do not land together. See
   * `SignOutButton`, which puts the sign-out request between them.
   */
  clear: () => void;
  api: AuthApi;
}

const SessionContext = createContext<Session | null>(null);

interface Props {
  api: AuthApi;
  children: ReactNode;
}

export function AuthProvider({ api, children }: Props) {
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);

  const refresh = useCallback(async () => {
    try {
      setUser(await api.me());
    } catch {
      // An unreachable API is not a signed-in user, and the gate must fail
      // closed. It also must not spin: treating the failure as "signed out"
      // ends the check rather than leaving the app on a loading screen
      // forever.
      setUser(null);
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const clear = useCallback(() => setUser(null), []);

  const value = useMemo<Session>(
    () => ({ user, checking: user === undefined, refresh, clear, api }),
    [user, refresh, clear, api],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error("useSession was called outside an <AuthProvider>.");
  }
  return session;
}
